/*
 * Copyright (c) 2018, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
import "core-js";
import React from "react";
import ReactDOM from "react-dom";
import "semantic-ui-css/semantic.min.css";
import App from "./components/Common/App";
import "./index.css";
import { Provider } from "react-redux";
import { createStore, applyMiddleware, combineReducers, compose } from "redux";
import thunk from "redux-thunk";
import { BrowserRouter as Router } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import config from "./.samples.config";
import { AUTH_REDIRECT_URL_KEY } from "./helpers";
import {
  storeCredentialReducer,
  errorReducer
} from "./components/Common/reducers";
import {
  selectedReportTypeReducer,
  selectedActivityTypeReducer,
  learnersReducer,
  learnerReducer,
  selectedActivitiesReducer,
  activitiesReducer,
  fetchLovReducer,
  selectedEmpStatusReducer,
  selectedEmpTypesReducer,
  selectedRegulatoryBodiesReducer,
  selectedAssignmentTypesReducer,
  selectedCertificationsReducer,
  selectedAssignmentStatusReducer,
  assignmentsReducer,
  selectedAssignmentReducer,
  fetchedReducer,
  summaryReducer,
  reportReducer,
  drillDownReducer,
  fetchExcelReducer,
  fetchPDFReducer,
  selectedReportViewReducer,
  staffReducer,
  setUserModeReducer,
  fetchEmpKeyReducer,
  isSummaryGeneratedReducer,
  summaryForFilterReducer,
  thresholdHitReducer,
  fetchActivityKeyReducer,
  setSummaryExecutionTimeReducer,
  drilldownExecutionTimeReducer,
  reportExecutionTimeReducer
} from "./components/SASReport/reducers";

import {
  selectedStaffFunctionReducer,
  fetchStaffFunctionsReducer,
  fetchManagersReducer,
  selectedManagersReducer,
  fetchAVDataReducer,
  generateAVReportPdfReducer,
  generateAVReportExcelReducer,
  selectedAssignmentVehicleReducer,
  fetchLADataReducer,
  generateLAReportExcelReducer,
  generateLAReportPdfReducer,
  selectedAVManagersReducer,
  selectedAVsReducer,
  selectedStaffsReducer,
  selectedCompletionStatusReducer,
  selectedLocationsReducer,
  selectedCostCentersReducer,
  setAVExecutionTimeReducer,
  setLAExecutionTimeReducer
} from "./components/TASReport/reducers";

const sasReducers = {
  selectedReportView: selectedReportViewReducer,
  selectedReportType: selectedReportTypeReducer,
  selectedActivityType: selectedActivityTypeReducer,
  learners: learnersReducer,
  selectedLearners: learnerReducer,
  fetchedStatuses: fetchedReducer,
  activities: activitiesReducer,
  selectedActivities: selectedActivitiesReducer,
  lovs: fetchLovReducer,
  selectedEmpStatuses: selectedEmpStatusReducer,
  selectedEmpTypes: selectedEmpTypesReducer,
  selectedRegulatoryBodies: selectedRegulatoryBodiesReducer,
  selectedAssignmentTypes: selectedAssignmentTypesReducer,
  selectedCertifications: selectedCertificationsReducer,
  selectedAssignmentStatuses: selectedAssignmentStatusReducer,
  assignments: assignmentsReducer,
  selectedAssignments: selectedAssignmentReducer,
  summaryInfo: summaryReducer,
  reportInfo: reportReducer,
  drillDownInfo: drillDownReducer,
  fetchExcel: fetchExcelReducer,
  fetchPDF: fetchPDFReducer,
  selectedStaff: staffReducer,
  selectedUserMode: setUserModeReducer,
  isSummaryForFilterGenerated: isSummaryGeneratedReducer,
  summariesForFilter: summaryForFilterReducer,
  isThresholdHit: thresholdHitReducer,
  empkey: fetchEmpKeyReducer,
  activitykey: fetchActivityKeyReducer,
  summaryQueryExecutionTime: setSummaryExecutionTimeReducer,
  reportQueryExecutionTime: reportExecutionTimeReducer,
  drilldownQueryExecutionTime: drilldownExecutionTimeReducer
};

const tasReducers = {
  selectedStaffFunction: selectedStaffFunctionReducer,
  staffFunctions: fetchStaffFunctionsReducer,
  staffManagers: fetchManagersReducer,
  selectedManagers: selectedManagersReducer,
  avReportData: fetchAVDataReducer,
  avpdf: generateAVReportPdfReducer,
  avexcel: generateAVReportExcelReducer,
  selectedAssignmentVehicle: selectedAssignmentVehicleReducer,
  laReportData: fetchLADataReducer,
  lapdf: generateLAReportPdfReducer,
  laexcel: generateLAReportExcelReducer,
  selectedAVManagers: selectedAVManagersReducer,
  selectedAVs: selectedAVsReducer,
  selectedStaffs: selectedStaffsReducer,
  selectedLocations: selectedLocationsReducer,
  selectedCostCenters: selectedCostCentersReducer,
  selectedCompletionStatus: selectedCompletionStatusReducer,
  avQueryExecutionTime: setAVExecutionTimeReducer,
  laQueryExecutionTime: setLAExecutionTimeReducer
};

const combinedReducers = combineReducers({
  ...sasReducers,
  ...tasReducers,
  credential: storeCredentialReducer,
  errors: errorReducer
});

/* export const rootReducer = (state, action) => {
  if (action.type === 'RESET_APP') {
    state = undefined;
  }
  return combinedReducers(state, action);
} */
/* global document */
/* eslint-disable react/jsx-filename-extension */
//const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSIONS_COMPOSE__ || compose;
const composeEnhancers =
  typeof window === "object" && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        // Specify extension’s options like name, actionsBlacklist, actionsCreators, serialize...
      })
    : compose;
const store = createStore(
  combinedReducers,
  composeEnhancers(applyMiddleware(thunk))
);
const msalInstance = new PublicClientApplication({
  auth: {
    ...config.msal.auth,
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false
  }
});

function restoreOriginalUrlAfterLogin() {
  const redirectUrl = window.sessionStorage.getItem(AUTH_REDIRECT_URL_KEY);

  if (!redirectUrl) {
    return;
  }

  window.sessionStorage.removeItem(AUTH_REDIRECT_URL_KEY);

  try {
    const targetUrl = new URL(redirectUrl, window.location.origin);

    if (targetUrl.origin !== window.location.origin) {
      return;
    }

    const targetPath = targetUrl.pathname + targetUrl.search + targetUrl.hash;
    const currentPath =
      window.location.pathname + window.location.search + window.location.hash;

    if (targetPath !== currentPath) {
      window.history.replaceState(null, document.title, targetPath);
    }
  } catch (error) {
    console.warn("Unable to restore original URL after login", error);
  }
}

function renderApp() {
  ReactDOM.render(
    <Provider store={store}>
      <Router>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </Router>
    </Provider>,
    document.getElementById("root")
  );
}

msalInstance
  .handleRedirectPromise()
  .then(response => {
    const account =
      (response && response.account) || msalInstance.getAllAccounts()[0];

    if (account) {
      msalInstance.setActiveAccount(account);
      restoreOriginalUrlAfterLogin();
    }

    renderApp();
  })
  .catch(error => {
    console.error("MSAL redirect handling failed", error);
    renderApp();
  });
